import { useMutation } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";

import { Alert } from "../../components/ui/Alert";
import { Button } from "../../components/ui/Button";
import { DateTimeField } from "../../components/ui/DateTimeField";
import { FormField } from "../../components/ui/FormField";
import { Input } from "../../components/ui/Input";
import { ApiError } from "../../lib/api";
import { type EnrollmentMethod, enrollmentApi } from "../../lib/api/enrollment";
import { t } from "../../lib/i18n";

interface CreateBatchFormProps {
  onCreated: () => void;
  onCancel: () => void;
}

const METHODS: EnrollmentMethod[] = ["qr", "invite", "code"];

export function CreateBatchForm({ onCreated, onCancel }: CreateBatchFormProps) {
  const [method, setMethod] = useState<EnrollmentMethod>("qr");
  const [label, setLabel] = useState("");
  const [maxUses, setMaxUses] = useState<string>("");
  const [expiresAt, setExpiresAt] = useState<string>("");

  const mutate = useMutation({
    mutationFn: () =>
      enrollmentApi.createBatch({
        method,
        label: label || undefined,
        max_uses: maxUses ? Number(maxUses) : null,
        expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
      }),
    onSuccess: onCreated,
  });

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    mutate.mutate();
  };

  const errMsg =
    mutate.error instanceof ApiError
      ? t(`errors.${mutate.error.code}`, mutate.error.message)
      : mutate.error
        ? t("errors.network")
        : null;

  return (
    <form onSubmit={onSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {errMsg && <Alert tone="danger">{errMsg}</Alert>}

      <FormField label={t("enrollment.create.field.method")} required>
        {(id) => (
          <select
            id={id}
            className="input"
            value={method}
            onChange={(e) => setMethod(e.target.value as EnrollmentMethod)}
          >
            {METHODS.map((m) => (
              <option key={m} value={m}>
                {t(`enrollment.method.${m}`)}
              </option>
            ))}
          </select>
        )}
      </FormField>

      <FormField label={t("enrollment.create.field.label")}>
        {(id) => (
          <Input
            id={id}
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder={t("enrollment.create.placeholder.label")}
          />
        )}
      </FormField>

      <FormField
        label={t("enrollment.create.field.maxUses")}
        hint={t("enrollment.create.hint.maxUses")}
      >
        {(id) => (
          <Input
            id={id}
            type="number"
            min={1}
            value={maxUses}
            onChange={(e) => setMaxUses(e.target.value)}
            placeholder={t("enrollment.create.placeholder.maxUses")}
          />
        )}
      </FormField>

      <FormField label={t("enrollment.create.field.expiresAt")}>
        {(id) => (
          <DateTimeField
            id={id}
            value={expiresAt}
            onChange={setExpiresAt}
            ariaLabel={t("enrollment.create.field.expiresAt")}
          />
        )}
      </FormField>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <Button variant="ghost" type="button" onClick={onCancel}>
          {t("common.cancel")}
        </Button>
        <Button type="submit" loading={mutate.isPending}>
          {t("enrollment.create.submit")}
        </Button>
      </div>
    </form>
  );
}
